import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";

const PAGES = [
  { src: "/assets/brochure.jpeg", alt: "Brochure JLC — Présentation & Coordonnées" },
  { src: "/assets/brochure-page2.jpeg", alt: "Brochure JLC — Climatiseurs & Tarifs" },
];

/**
 * 3D Flipbook with CSS page-turn animation.
 * Two-sided pages rotate around the book spine (Y axis).
 */
const Flipbook = () => {
  const [page, setPage] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [flipping, setFlipping] = useState(null); // "next" | "prev" | null
  const total = PAGES.length;

  const go = (dir) => {
    if (flipping) return;
    if (dir === "next" && page < total - 1) {
      setFlipping("next");
      setTimeout(() => {
        setPage((p) => p + 1);
        setFlipping(null);
      }, 650);
    }
    if (dir === "prev" && page > 0) {
      setFlipping("prev");
      setTimeout(() => {
        setPage((p) => p - 1);
        setFlipping(null);
      }, 650);
    }
  };

  return (
    <>
      <div className="relative w-full max-w-2xl mx-auto" data-testid="flipbook">
        {/* Book container with 3D perspective */}
        <div
          className="relative aspect-[1600/1131] w-full"
          style={{ perspective: "2400px" }}
        >
          {/* Book shadow */}
          <div className="absolute inset-x-6 -bottom-4 h-10 bg-black/40 blur-2xl rounded-full" />

          {/* Stack of pages - static back */}
          <div className="absolute inset-0 rounded-xl overflow-hidden shadow-2xl bg-white">
            <img
              src={PAGES[page].src}
              alt={PAGES[page].alt}
              className="w-full h-full object-contain bg-white"
              draggable={false}
            />
          </div>

          {/* Animated flipping page on top */}
          {flipping === "next" && page < total - 1 && (
            <div
              className="absolute inset-0 rounded-xl overflow-hidden shadow-2xl bg-white origin-left"
              style={{
                transformStyle: "preserve-3d",
                animation: "flipRight 0.65s cubic-bezier(0.4, 0, 0.2, 1) forwards",
                zIndex: 20,
              }}
            >
              <img
                src={PAGES[page].src}
                alt=""
                className="w-full h-full object-contain bg-white"
                draggable={false}
                style={{ backfaceVisibility: "hidden" }}
              />
              {/* Back side shows next page when rotated */}
              <div
                className="absolute inset-0"
                style={{
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                }}
              >
                <img
                  src={PAGES[page + 1].src}
                  alt=""
                  className="w-full h-full object-contain bg-white"
                  draggable={false}
                />
              </div>
              {/* Shadow gradient during flip */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
                  animation: "flipShade 0.65s ease-in-out forwards",
                }}
              />
            </div>
          )}

          {flipping === "prev" && page > 0 && (
            <div
              className="absolute inset-0 rounded-xl overflow-hidden shadow-2xl bg-white origin-right"
              style={{
                transformStyle: "preserve-3d",
                animation: "flipLeft 0.65s cubic-bezier(0.4, 0, 0.2, 1) forwards",
                zIndex: 20,
              }}
            >
              <img
                src={PAGES[page].src}
                alt=""
                className="w-full h-full object-contain bg-white"
                draggable={false}
                style={{ backfaceVisibility: "hidden" }}
              />
              <div
                className="absolute inset-0"
                style={{
                  backfaceVisibility: "hidden",
                  transform: "rotateY(-180deg)",
                }}
              >
                <img
                  src={PAGES[page - 1].src}
                  alt=""
                  className="w-full h-full object-contain bg-white"
                  draggable={false}
                />
              </div>
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(to left, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 100%)",
                  animation: "flipShadeLeft 0.65s ease-in-out forwards",
                }}
              />
            </div>
          )}

          {/* Side shadows on the static book */}
          <div className="absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-black/25 to-transparent rounded-l-xl pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-black/10 to-transparent rounded-r-xl pointer-events-none" />

          {/* Fullscreen button */}
          <button
            onClick={() => setFullscreen(true)}
            data-testid="flipbook-fullscreen"
            aria-label="Plein écran"
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center bg-white/90 hover:bg-white text-[color:var(--jlc-blue-ink)] rounded-full shadow-lg transition-all hover:scale-110 z-30"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
          </button>
        </div>

        {/* Controls */}
        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            onClick={() => go("prev")}
            disabled={page === 0 || flipping}
            data-testid="flipbook-prev"
            className="h-11 w-11 grid place-items-center rounded-full bg-white text-[color:var(--jlc-blue-ink)] shadow-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[color:var(--jlc-cyan)] hover:text-white transition-all"
            aria-label="Page précédente"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2">
            {PAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  if (flipping || i === page) return;
                  go(i > page ? "next" : "prev");
                }}
                aria-label={`Aller à la page ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === page ? "w-8 bg-white" : "w-2 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
            <span className="ml-3 text-white/70 text-xs font-semibold tracking-wider">
              {String(page + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </div>

          <button
            onClick={() => go("next")}
            disabled={page === total - 1 || flipping}
            data-testid="flipbook-next"
            className="h-11 w-11 grid place-items-center rounded-full bg-white text-[color:var(--jlc-blue-ink)] shadow-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[color:var(--jlc-cyan)] hover:text-white transition-all"
            aria-label="Page suivante"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Download link */}
        <a
          href="/assets/brochure.jpeg"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="flipbook-download"
          className="mt-4 inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold w-full justify-center"
        >
          <Download className="h-4 w-4" /> Télécharger la brochure complète
        </a>
      </div>

      {/* Fullscreen modal */}
      {fullscreen && (
        <div
          data-testid="flipbook-modal"
          className="fixed inset-0 z-[100] bg-black/95 grid place-items-center p-4 md:p-10 animate-[fadeIn_0.3s_ease]"
          onClick={() => setFullscreen(false)}
        >
          <button
            onClick={() => setFullscreen(false)}
            aria-label="Fermer"
            className="absolute top-5 right-5 h-11 w-11 grid place-items-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={PAGES[page].src}
            alt={PAGES[page].alt}
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-5 inset-x-0 flex items-center justify-center gap-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                go("prev");
              }}
              disabled={page === 0}
              className="h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-colors"
              aria-label="Page précédente"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <span className="text-white/80 text-sm font-semibold tracking-wider">
              {page + 1} / {total}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                go("next");
              }}
              disabled={page === total - 1}
              className="h-12 w-12 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white disabled:opacity-30 transition-colors"
              aria-label="Page suivante"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes flipRight {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(-180deg); }
        }
        @keyframes flipLeft {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(180deg); }
        }
        @keyframes flipShade {
          0% { opacity: 0; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes flipShadeLeft {
          0% { opacity: 0; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default Flipbook;
